import { useState, useEffect, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  IconButton,
  Menu,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DescriptionIcon from "@mui/icons-material/Description";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import API, { getErrorMessage } from "../api/axios";
import { useToast } from "../components/Toast";
import { ACCENT, NAVY } from "../theme";
import PageContainer from "../components/ui/PageContainer";
import LoadingState from "../components/ui/LoadingState";
import EmptyState from "../components/ui/EmptyState";
import SearchInput from "../components/ui/SearchInput";
import FilterBar from "../components/ui/FilterBar";
import StatusBadge from "../components/ui/StatusBadge";

// ─── Candidate.feedback — confirmed against the real backend enum
// (Candidate.java) before writing any of this file. The real values are
// PENDING, SHORTLISTED, SELECTED, L1_ROUND, L2_ROUND, ON_HOLD, REJECTED,
// DUPLICATE — there is no single "INTERVIEW" stage; interviews are split
// into two rounds. Ordered here as a logical pipeline progression, with the
// three terminal/side states (SELECTED, REJECTED, DUPLICATE) at the end.
// This list, STATUS_COLOR, and statusLabel are intentionally identical to
// the ones already defined in Candidates.jsx, so a candidate's stage always
// renders with the same label/color everywhere in the app.
const STAGES = [
  "PENDING",
  "SHORTLISTED",
  "L1_ROUND",
  "L2_ROUND",
  "ON_HOLD",
  "SELECTED",
  "REJECTED",
  "DUPLICATE",
];

const STATUS_COLOR = {
  PENDING: "default",
  SHORTLISTED: "info",
  L1_ROUND: "primary",
  L2_ROUND: "primary",
  ON_HOLD: "warning",
  SELECTED: "success",
  REJECTED: "error",
  DUPLICATE: "secondary",
};

const statusLabel = (k) =>
  ({
    PENDING: "Pending",
    SHORTLISTED: "Shortlisted",
    L1_ROUND: "L1 Round",
    L2_ROUND: "L2 Round",
    ON_HOLD: "On Hold",
    SELECTED: "Selected",
    REJECTED: "Rejected",
    DUPLICATE: "Duplicate",
  })[k] || k;

// Same mapping used on every other redesigned page.
const MUI_COLOR_TO_TONE = {
  success: "ok",
  info: "info",
  primary: "info",
  warning: "warn",
  error: "danger",
  default: "neutral",
  secondary: "neutral",
};

// ── Phase 9 (Design Gap Audit item I): visual-parity additions only ──────────
// Colored dot per stage, matching the prototype's kcol header dot. Colors
// chosen to stay distinguishable from the STATUS_COLOR/StatusBadge palette
// already used elsewhere (this is purely a column-header accent, it does
// not replace or duplicate the StatusBadge's own semantic color).
const STAGE_DOT_COLOR = {
  PENDING: "#94a3b8",
  SHORTLISTED: "#2563eb",
  L1_ROUND: "#7c3aed",
  L2_ROUND: "#0d9488",
  ON_HOLD: "#d97706",
  SELECTED: "#059669",
  REJECTED: "#dc2626",
  DUPLICATE: "#6b7280",
};

// Small helper for the card's initials avatar (visual only — no new data).
function initials(c) {
  const a = (c.firstName || "").trim()[0] || "";
  const b = (c.lastName || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}
const AVATAR_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#0d9488",
  "#d97706",
  "#059669",
  "#dc2626",
  "#4f46e5",
  "#0891b2",
];

export default function Pipeline() {
  const toast = useToast();

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [jobCodeFilter, setJobCodeFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [movingId, setMovingId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCandidate, setMenuCandidate] = useState(null);

  // ── Company filter data (new) ────────────────────────────────────────────
  // IMPORTANT: candidate.companyName is the candidate's OWN current
  // employer (see Candidates.jsx's "Current Company" column) — it is NOT
  // the hiring client company for the position they're pipelined against.
  // The real relationship, confirmed by reading JobRequirement.java, is
  // Candidate -> jobCode -> JobRequirement -> companyName. So this loads
  // /jobs (the same existing endpoint JobRequirements.jsx, Clients.jsx, and
  // Users.jsx already call — no new backend endpoint) purely to build that
  // jobCode -> companyName lookup for filtering. It does not touch
  // `candidates` state or `fetchAll` above in any way.
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    API.get("/jobs")
      .then((r) => setJobs(r.data || []))
      .catch(() => setJobs([])); // non-critical — Company filter just won't have options if this fails
  }, []);
  const jobCodeToCompany = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      if (j.jobCode) map[j.jobCode] = j.companyName || "";
    });
    return map;
  }, [jobs]);

  // Same endpoint Candidates.jsx already uses. The backend enforces
  // ownership scoping (RECRUITER/STAFF see only their own candidates,
  // ADMIN/MANAGER see all) — this page does no client-side filtering by
  // owner, exactly like Candidates.jsx, so it never shows more than the
  // backend already authorizes.
  const fetchAll = async () => {
    try {
      const res = await API.get("/candidates");
      setCandidates(res.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not load pipeline"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobCodeOptions = useMemo(
    () =>
      Array.from(
        new Set(candidates.map((c) => c.jobCode).filter(Boolean)),
      ).sort(),
    [candidates],
  );

  // Real companies — derived from the jobs actually referenced by current
  // pipeline candidates (via jobCodeToCompany), so every option shown will
  // actually match at least one candidate. No hardcoded/fake entries.
  const companyOptions = useMemo(
    () =>
      Array.from(
        new Set(
          candidates.map((c) => jobCodeToCompany[c.jobCode]).filter(Boolean),
        ),
      ).sort(),
    [candidates, jobCodeToCompany],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter((c) => {
      if (jobCodeFilter && c.jobCode !== jobCodeFilter) return false;
      if (companyFilter && jobCodeToCompany[c.jobCode] !== companyFilter)
        return false;
      if (!q) return true;
      const name = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      return (
        name.includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.jobCode || "").toLowerCase().includes(q) ||
        (c.companyName || "").toLowerCase().includes(q) ||
        (jobCodeToCompany[c.jobCode] || "").toLowerCase().includes(q)
      );
    });
  }, [candidates, search, jobCodeFilter, companyFilter, jobCodeToCompany]);

  const byStage = useMemo(() => {
    const groups = Object.fromEntries(STAGES.map((s) => [s, []]));
    visible.forEach((c) => {
      const key = STAGES.includes(c.feedback) ? c.feedback : "PENDING";
      groups[key].push(c);
    });
    return groups;
  }, [visible]);

  const openMoveMenu = (event, candidate) => {
    setMenuAnchor(event.currentTarget);
    setMenuCandidate(candidate);
  };
  const closeMoveMenu = () => {
    setMenuAnchor(null);
    setMenuCandidate(null);
  };

  // Stage-change: reuses the existing PUT /api/candidates/{id} endpoint —
  // the same one Candidates.jsx's own edit form uses. That endpoint (and
  // CandidateService.updateCandidate underneath it) does a full field-by-
  // field overwrite of the candidate, NOT a partial merge — confirmed by
  // reading the actual service code before writing this. Sending only
  // { feedback: "X" } would null out every other field on the candidate.
  // So the full existing candidate object (already returned in whole by
  // GET /api/candidates, minus resume bytes which this endpoint doesn't
  // touch anyway) is sent back unchanged except for `feedback` — exactly
  // mirroring Candidates.jsx's own onSave() pattern.
  const moveCandidate = async (candidate, newStage) => {
    if (candidate.feedback === newStage) {
      closeMoveMenu();
      return;
    }
    setMovingId(candidate.id);
    try {
      const payload = { ...candidate, feedback: newStage };
      await API.put(`/candidates/${candidate.id}`, payload);
      setCandidates((prev) =>
        prev.map((c) =>
          c.id === candidate.id ? { ...c, feedback: newStage } : c,
        ),
      );
      toast.success(`Moved to ${statusLabel(newStage)}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not update candidate stage"));
    } finally {
      setMovingId(null);
      closeMoveMenu();
    }
  };

  // Drag-and-drop entry point. Reuses moveCandidate() above — the EXACT
  // same function the existing ⋮ menu already calls — so there is only one
  // stage-update implementation in this file, not two. Guards required by
  // spec:
  //   - dropped outside any valid column (result.destination is null) → no-op
  //   - dropped back in the same column → no-op (moveCandidate itself also
  //     no-ops on same-stage, this is just a faster early exit)
  //   - the candidate is looked up fresh from `visible` by ID rather than
  //     trusted from the drag event, so the full-object PUT always sends a
  //     complete, current candidate record — never a partial/stale one.
  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return; // dropped outside any droppable area
    if (source.droppableId === destination.droppableId) return; // same column
    const candidate = visible.find((c) => String(c.id) === draggableId);
    if (!candidate) return;
    moveCandidate(candidate, destination.droppableId);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <LoadingState variant="spinner" label="Loading pipeline…" />
      </Box>
    );
  }

  return (
    <PageContainer sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <ViewKanbanOutlinedIcon sx={{ color: ACCENT }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY }}>
          Pipeline
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
        Use the ⋮ menu on a card to move a candidate to a different stage ·
        showing {visible.length} candidate{visible.length !== 1 ? "s" : ""}
      </Typography>

      {/* Search + job filter — client-side, same fields Candidates.jsx
          already searches on. No new API call. */}
      <FilterBar
        activeCount={[jobCodeFilter, companyFilter].filter(Boolean).length}
        onClear={() => {
          setJobCodeFilter("");
          setCompanyFilter("");
        }}
      >
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search candidate, job code or company…"
          sx={{ flex: 2, minWidth: 220 }}
        />
        <TextField
          select
          label="Job Code"
          size="small"
          value={jobCodeFilter}
          onChange={(e) => setJobCodeFilter(e.target.value)}
          sx={{ flex: 1, minWidth: 160 }}
        >
          <MenuItem value="">All Jobs</MenuItem>
          {jobCodeOptions.map((jc) => (
            <MenuItem key={jc} value={jc}>
              {jc}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Company"
          size="small"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          sx={{ flex: 1, minWidth: 160 }}
        >
          <MenuItem value="">All Companies</MenuItem>
          {companyOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </FilterBar>

      {visible.length === 0 ? (
        <Paper sx={{ py: 2 }}>
          <EmptyState
            icon={
              <ViewKanbanOutlinedIcon sx={{ fontSize: 28, color: ACCENT }} />
            }
            title="No candidates found"
            description={
              search || jobCodeFilter || companyFilter
                ? "Try clearing your search, job, or company filter."
                : "Candidates will appear here as they're added."
            }
          />
        </Paper>
      ) : (
        // Horizontal-scroll board: the same, well-established low-risk
        // approach to a responsive Kanban layout — every column keeps its
        // full width and functionality at any screen size, the user just
        // scrolls sideways on narrow viewports instead of the layout
        // breaking or columns being dropped.
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              // Matches Positions' (JobRequirements.jsx) proven scroll pattern
              // exactly: a bounded maxHeight makes this box its own
              // self-contained scroll region (both directions), with an
              // ALWAYS-VISIBLE, easy-to-grab scrollbar — rather than relying on
              // the browser's default overlay scrollbar, which on many
              // systems is thin, auto-hiding, and easy to miss entirely (this
              // was the actual root cause of columns appearing "cut off with
              // no way to scroll" — the scroll worked, but the affordance to
              // discover/use it was invisible). No column, card, or stage
              // logic changes — only the scroll container's CSS.
              maxHeight: "calc(100vh - 175px)",
              overflowX: "scroll",
              overflowY: "auto",
              pb: 1,
              // WebKit (Chrome/Edge): force the horizontal AND vertical bar to
              // always render, identical to Positions.
              "&::-webkit-scrollbar": {
                height: 12,
                width: 12,
                WebkitAppearance: "none",
                display: "block",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e2e8f0",
                borderRadius: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#94a3b8",
                borderRadius: 8,
                border: "2px solid #e2e8f0",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#64748b",
              },
              // Firefox
              scrollbarWidth: "auto",
              scrollbarColor: "#94a3b8 #e2e8f0",
            }}
          >
            {STAGES.map((stage) => {
              const items = byStage[stage];
              return (
                <Box
                  key={stage}
                  sx={{
                    minWidth: 280,
                    maxWidth: 280,
                    flexShrink: 0,
                    bgcolor: "#f2f4f7",
                    borderRadius: "12px",
                    p: 1.25,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1, px: 0.5 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: STAGE_DOT_COLOR[stage],
                          flexShrink: 0,
                        }}
                      />
                      <StatusBadge
                        label={statusLabel(stage)}
                        tone={
                          MUI_COLOR_TO_TONE[STATUS_COLOR[stage]] || "neutral"
                        }
                      />
                    </Stack>
                    <Chip
                      size="small"
                      label={items.length}
                      sx={{
                        fontWeight: 700,
                        height: 20,
                        fontSize: 11,
                        bgcolor: "#fff",
                      }}
                    />
                  </Stack>

                  <Droppable droppableId={stage}>
                    {(provided, snapshot) => (
                      <Stack
                        spacing={1}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: 40,
                          borderRadius: "8px",
                          bgcolor: snapshot.isDraggingOver
                            ? "rgba(37,99,235,0.08)"
                            : "transparent",
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        {items.length === 0 ? (
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              color: "text.secondary",
                              textAlign: "center",
                              py: 2,
                            }}
                          >
                            No candidates
                          </Typography>
                        ) : (
                          items.map((c, index) => (
                            <Draggable
                              key={c.id}
                              draggableId={String(c.id)}
                              index={index}
                            >
                              {(dragProvided, dragSnapshot) => (
                                <Paper
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  variant="outlined"
                                  sx={{
                                    p: 1.25,
                                    borderRadius: "8px",
                                    position: "relative",
                                    opacity: movingId === c.id ? 0.6 : 1,
                                    boxShadow: dragSnapshot.isDragging
                                      ? "0 8px 20px rgba(15,27,51,0.18)"
                                      : "0 1px 2px rgba(15,27,51,0.05)",
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 0.5 }}
                                  >
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        bgcolor:
                                          AVATAR_COLORS[
                                            c.id % AVATAR_COLORS.length
                                          ] || ACCENT,
                                        color: "#fff",
                                        fontSize: 10.5,
                                        fontWeight: 700,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {initials(c)}
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: 13.5,
                                        lineHeight: 1.3,
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {c.firstName} {c.lastName}
                                    </Typography>
                                    {movingId === c.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => openMoveMenu(e, c)}
                                        sx={{
                                          mt: -0.5,
                                          mr: -0.5,
                                          flexShrink: 0,
                                        }}
                                      >
                                        <MoreVertIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Stack>

                                  {(c.jobCode || c.companyName) && (
                                    <Typography
                                      sx={{
                                        fontSize: 11.5,
                                        color: "text.secondary",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {[c.jobCode, c.companyName]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </Typography>
                                  )}

                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    flexWrap="wrap"
                                    sx={{ mt: 0.75, rowGap: 0.5 }}
                                  >
                                    {c.totalExperience && (
                                      <Chip
                                        size="small"
                                        label={c.totalExperience}
                                        sx={{ height: 19, fontSize: 10.5 }}
                                      />
                                    )}
                                    {c.currentSalary && (
                                      <Chip
                                        size="small"
                                        label={c.currentSalary}
                                        sx={{ height: 19, fontSize: 10.5 }}
                                      />
                                    )}
                                    {c.resumeFilename && (
                                      <Tooltip title="Resume on file">
                                        <Chip
                                          size="small"
                                          icon={
                                            <DescriptionIcon
                                              sx={{ fontSize: 12 }}
                                            />
                                          }
                                          label="Resume"
                                          sx={{ height: 19, fontSize: 10.5 }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </Paper>
                              )}
                            </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </Box>
              );
            })}
          </Box>
        </DragDropContext>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMoveMenu}
      >
        {STAGES.filter((s) => s !== menuCandidate?.feedback).map((s) => (
          <MenuItem key={s} onClick={() => moveCandidate(menuCandidate, s)}>
            Move to {statusLabel(s)}
          </MenuItem>
        ))}
      </Menu>
    </PageContainer>
  );
}
